import { formatLocalizedDateTimeWithShortTz } from '@/lib/formatLocalizedDateTimeWithShortTz';
import { cn } from '@/lib/utils';

type UsageState = 'normal' | 'warning' | 'blocked';

type TokenTransferLimitViewModel = {
  state: UsageState;
  usedPct: number;
  pendingPct: number;
  hasPendingAmount: boolean;
  projectedUsedDisplay: string;
  remainingDisplay: string;
  overLimitDisplay: string;
  capDisplay: string;
  resetAtDisplay: string;
};

export interface TokenTransferLimitProps {
  /** Maximum amount allowed in the active policy window, formatted for display. */
  policyLimit: string;
  /** Amount already consumed in the current rate-limit window, formatted for display. */
  windowUsed: string;
  /** Human-readable amount the user is currently entering. Use "0" for read-only displays. */
  currentTransactionAmount: string;
  /** Token symbol shown next to formatted amounts. */
  tokenSymbol: string;
  /** Reset time of the active usage window (only read when {@link showResetTimer} is true). */
  windowResetAt: Date;
  className?: string;
  /** When false, hides the footer line (`resets at …`). Defaults to true. */
  showResetTimer?: boolean;
  /**
   * `default`: copy assumes a pending transfer may apply ("left after this transfer").
   * `windowUsage`: read-only copy for cumulative window limits ("remaining in window").
   */
  copyVariant?: 'default' | 'windowUsage';
}

/**
 * Shortens fractional display strings by capping fractional digits (max 4) and stripping trailing zeros.
 *
 * @example
 * trimAmountForDisplay('10'); // '10'
 * @example
 * trimAmountForDisplay('1.234500'); // '1.2345'
 * @example
 * trimAmountForDisplay('99.1000'); // '99.1'
 */
function trimAmountForDisplay(amount: string): string {
  if (!amount.includes('.')) {
    return amount;
  }

  const [whole = '', fraction = ''] = amount.split('.');
  const compactFraction = fraction.slice(0, 4).replace(/0+$/, '');
  return compactFraction ? `${whole}.${compactFraction}` : whole;
}

/**
 * Counts digits after the decimal point for aligning amounts before fixed-point math.
 *
 * @example
 * getFractionDigitCount('100'); // 0
 * @example
 * getFractionDigitCount('0.0042'); // 4
 */
function getFractionDigitCount(amount: string): number {
  return amount.includes('.') ? (amount.split('.')[1]?.length ?? 0) : 0;
}

/**
 * Parses a decimal string into a fixed-point integer (`value * 10^scale`) for exact bigint arithmetic.
 * Empty or whitespace-only input becomes zero; fractional digits beyond `scale` are truncated.
 *
 * @example
 * toScaledAmount('1.5', 2); // 150n
 * @example
 * toScaledAmount('  ', 4); // 0n
 * @example
 * toScaledAmount('2.999', 2); // 299n
 */
function toScaledAmount(amount: string, scale: number): bigint {
  const normalized = amount.trim() === '' ? '0' : amount.trim();
  const [whole = '0', fraction = ''] = normalized.split('.');
  const scaledFraction = fraction.padEnd(scale, '0').slice(0, scale);
  return BigInt(`${whole}${scaledFraction}` || '0');
}

/**
 * Converts a fixed-point bigint back to a decimal string with up to `scale` fractional digits.
 * Trailing fractional zeros are omitted (`scale` 0 yields integer strings only).
 *
 * @example
 * formatScaledAmount(150n, 2); // '1.5'
 * @example
 * formatScaledAmount(100n, 2); // '1'
 * @example
 * formatScaledAmount(42n, 0); // '42'
 */
function formatScaledAmount(amount: bigint, scale: number): string {
  if (scale === 0) {
    return amount.toString();
  }

  // Preserve sign separately so fixed-point slicing operates on unsigned digits.
  const isNegative = amount < 0n;
  const absoluteAmount = isNegative ? -amount : amount;
  const amountString = absoluteAmount.toString().padStart(scale + 1, '0');
  const whole = amountString.slice(0, -scale);
  const fraction = amountString.slice(-scale).replace(/0+$/, '');
  const unsigned = fraction ? `${whole}.${fraction}` : whole;
  return isNegative ? `-${unsigned}` : unsigned;
}

/**
 * Derives UI severity from projected remaining capacity after the pending transfer.
 * - `blocked`: cumulative used + pending exceeds the cap.
 * - `warning`: there is a pending amount, or remaining is ≤ 20% of the cap (and cap > 0).
 * - `normal`: otherwise.
 *
 * @example
 * getUsageState({ projectedRemainingRaw: -1n, capRaw: 100n, hasPendingAmount: true }); // 'blocked'
 * @example
 * getUsageState({ projectedRemainingRaw: 10n, capRaw: 100n, hasPendingAmount: false }); // 'warning' (≤20% left)
 * @example
 * getUsageState({ projectedRemainingRaw: 50n, capRaw: 100n, hasPendingAmount: false }); // 'normal'
 */
function getUsageState(input: {
  projectedRemainingRaw: bigint;
  capRaw: bigint;
  hasPendingAmount: boolean;
}): UsageState {
  if (input.projectedRemainingRaw < 0n) {
    return 'blocked';
  }

  if (
    input.hasPendingAmount ||
    (input.capRaw > 0n && input.projectedRemainingRaw * 5n <= input.capRaw)
  ) {
    return 'warning';
  }

  return 'normal';
}

/**
 * Returns used portion of the cap as a percentage (0–100) for progress-bar width.
 * Zero used or non-positive cap yields 0; result is capped at 100.
 *
 * @example
 * getUsagePercent(25n, 100n); // 25
 * @example
 * getUsagePercent(0n, 100n); // 0
 * @example
 * getUsagePercent(150n, 100n); // 100
 */
function getUsagePercent(usedRaw: bigint, capRaw: bigint): number {
  if (capRaw <= 0n || usedRaw <= 0n) {
    return 0;
  }

  const basisPoints = (usedRaw * 10000n) / capRaw;
  return Math.min(100, Number(basisPoints) / 100);
}

/**
 * Builds display state for the limit meter: shared scale across limit/used/pending strings,
 * projected totals, remaining/over-cap amounts, bar percentages, and formatted reset timestamp.
 * Input strings are pre-formatted human amounts (same token); this helper only aligns precision.
 *
 * @example
 * buildTokenTransferLimitViewModel({
 *   policyLimit: '100',
 *   windowUsed: '30',
 *   currentTransactionAmount: '20',
 *   windowResetAt: new Date('2026-01-01T12:00:00Z'),
 * });
 * // state 'normal', usedPct 30, pendingPct 20, projectedUsedDisplay '50', remainingDisplay '50', …
 * @example
 * buildTokenTransferLimitViewModel({
 *   policyLimit: '10.0',
 *   windowUsed: '9',
 *   currentTransactionAmount: '2',
 *   windowResetAt: new Date(),
 * });
 * // state 'blocked', overLimitDisplay reflects excess beyond cap
 */
function buildTokenTransferLimitViewModel({
  policyLimit,
  windowUsed,
  currentTransactionAmount,
  windowResetAt,
}: Omit<
  TokenTransferLimitProps,
  'tokenSymbol' | 'className'
>): TokenTransferLimitViewModel {
  const scale = Math.max(
    getFractionDigitCount(policyLimit),
    getFractionDigitCount(windowUsed),
    getFractionDigitCount(currentTransactionAmount)
  );
  const capRaw = toScaledAmount(policyLimit, scale);
  const usedRaw = toScaledAmount(windowUsed, scale);
  const pendingRaw = toScaledAmount(currentTransactionAmount, scale);

  // Everything below is derived from the three source amounts: policy limit,
  // usage already recorded in the current window, and the in-progress transfer.
  const projectedUsedRaw = usedRaw + pendingRaw;
  const projectedRemainingRaw = capRaw - projectedUsedRaw;
  const overLimitRaw =
    projectedUsedRaw > capRaw ? projectedUsedRaw - capRaw : 0n;
  const usedPct = getUsagePercent(usedRaw, capRaw);
  const pendingPct = Math.min(
    100 - usedPct,
    getUsagePercent(pendingRaw, capRaw)
  );
  const resetAt = new Date(windowResetAt);

  return {
    state: getUsageState({
      projectedRemainingRaw,
      capRaw,
      hasPendingAmount: pendingRaw > 0n,
    }),
    usedPct,
    pendingPct,
    hasPendingAmount: pendingRaw > 0n,
    projectedUsedDisplay: trimAmountForDisplay(
      formatScaledAmount(projectedUsedRaw, scale)
    ),
    remainingDisplay: trimAmountForDisplay(
      formatScaledAmount(
        projectedRemainingRaw < 0n ? 0n : projectedRemainingRaw,
        scale
      )
    ),
    overLimitDisplay: trimAmountForDisplay(
      formatScaledAmount(overLimitRaw, scale)
    ),
    capDisplay: trimAmountForDisplay(formatScaledAmount(capRaw, scale)),
    resetAtDisplay: formatLocalizedDateTimeWithShortTz(resetAt),
  };
}

export function TokenTransferLimit({
  policyLimit,
  windowUsed,
  currentTransactionAmount,
  tokenSymbol,
  windowResetAt,
  className,
  showResetTimer = true,
  copyVariant = 'default',
}: TokenTransferLimitProps): React.ReactElement {
  const usageDisplay = buildTokenTransferLimitViewModel({
    policyLimit,
    windowUsed,
    currentTransactionAmount,
    windowResetAt,
  });
  const usageAccent = {
    normal: {
      text: 'text-zinc-600 dark:text-zinc-400',
      fill: 'bg-black',
    },
    warning: {
      text: 'text-amber-700 dark:text-amber-400',
      fill: 'bg-black',
    },
    blocked: {
      text: 'text-rose-700 dark:text-rose-400',
      fill: 'bg-black',
    },
  }[usageDisplay.state];
  const pendingBarClass =
    usageDisplay.state === 'blocked'
      ? 'bg-rose-600'
      : 'bg-[repeating-linear-gradient(135deg,theme(colors.amber.500)_0_5px,theme(colors.rose.500)_5px_10px)]';

  return (
    <div
      className={cn(
        'border-t border-zinc-200 dark:border-zinc-700 pt-2 mt-1 flex flex-col gap-1.5',
        className
      )}
    >
      {/* Header: projected usage after the pending transfer and remaining capacity. */}
      <div className="flex items-baseline justify-between gap-3 text-xs tabular-nums text-zinc-500 dark:text-zinc-400">
        <span
          className={
            usageDisplay.state === 'blocked' ? usageAccent.text : undefined
          }
        >
          {usageDisplay.state === 'blocked' ? (
            <>
              Over limit by{' '}
              <strong className={cn('font-semibold', usageAccent.text)}>
                {usageDisplay.overLimitDisplay} {tokenSymbol}
              </strong>
            </>
          ) : copyVariant === 'windowUsage' ? (
            <>
              <strong className="font-semibold text-zinc-900 dark:text-zinc-100">
                {usageDisplay.remainingDisplay} {tokenSymbol}
              </strong>{' '}
              remaining in window
            </>
          ) : (
            <>
              <strong className="font-semibold text-zinc-900 dark:text-zinc-100">
                {usageDisplay.remainingDisplay} {tokenSymbol}
              </strong>{' '}
              left after this transfer
            </>
          )}
        </span>
        <span className="font-medium">
          <span className="font-semibold text-zinc-900 dark:text-zinc-100">
            {usageDisplay.projectedUsedDisplay}
          </span>
          <span className="text-zinc-500/80 dark:text-zinc-400/80">
            {' '}
            / {usageDisplay.capDisplay} {tokenSymbol}
          </span>
        </span>
      </div>
      {/* Bar: black is already-used capacity; striped/solid red is the pending transfer. */}
      <div className="relative h-2.5 overflow-hidden rounded-md bg-zinc-900/[0.06] dark:bg-zinc-100/[0.08]">
        <div
          className={cn('absolute inset-y-0 left-0', usageAccent.fill)}
          style={{ width: `${usageDisplay.usedPct}%` }}
        />
        {usageDisplay.hasPendingAmount ? (
          <div
            className={cn(
              'absolute inset-y-0 border-l border-zinc-100/70 dark:border-zinc-800/70',
              pendingBarClass
            )}
            style={{
              left: `${usageDisplay.usedPct}%`,
              width: `${usageDisplay.pendingPct}%`,
            }}
          />
        ) : null}
      </div>
      {showResetTimer ? (
        <p
          className={cn(
            'text-[11.5px] tabular-nums',
            usageDisplay.state === 'normal'
              ? 'text-zinc-600 dark:text-zinc-400'
              : usageAccent.text
          )}
        >
          resets at {usageDisplay.resetAtDisplay}
        </p>
      ) : null}
    </div>
  );
}
